(() => {
  if (customElements.get('cb-atelier')) return;
  class Atelier extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      const read = key => JSON.parse(this.querySelector(`script[data-${key}]`).textContent);
      this.styles = read('styles');
      this.shells = read('shells');
      this.baseImages = read('base-images');
      this.purchaseConfig = read('purchase-config');
      this.stones = read('stones');
      this.cords = read('cords');
      this.beads = this.stones.map(stone => stone.key);
      this.state = {style: 'beaded', bead: this.stones[0]?.key || null, shell: null, cord: this.cords[0]?.key || null, wearing: 'clasp'};
      this.addEventListener('change', event => {
        if (!['style','bead','shell','cord','wearing'].includes(event.target.name)) return;
        this.state[event.target.name] = event.target.value;
        if (event.target.name === 'style') this.state.shell = null;
        if (['style','bead','shell'].includes(event.target.name)) this.state.wearing = 'clasp';
        this.querySelector('[data-status]').textContent = '';
        this.querySelector('[data-review-panel]').hidden = true;
        this.render();
      });
      this.querySelector('[data-review]').addEventListener('click', () => {
        if (!this.selection().shell) return;
        this.querySelector('[data-review-summary]').textContent = this.summary();
        this.querySelector('[data-review-panel]').hidden = false;
      });
      this.querySelector('[data-edit]').addEventListener('click', () => {
        this.querySelector('[data-review-panel]').hidden = true;
        this.querySelector('[data-review]').focus();
      });
      this.querySelector('form').addEventListener('submit', event => this.add(event));
      this.render();
    }
    compatible(shell, style) {
      return shell?.none === true ? style.key === 'beaded' : (style.key === 'cord' ? shell?.twoHole === true : shell?.pendantCompatible === true);
    }
    selection() {
      const style = this.styles.find(item => item.key === this.state.style) || this.styles[0];
      const shell = this.shells.find(item => item.key === this.state.shell && this.compatible(item,style));
      return {style,shell};
    }
    stone() {
      return this.stones.find(item => item.key === this.state.bead) || null;
    }
    cord() {
      return this.cords?.find(item => item.key === this.state.cord) || null;
    }
    preview() {
      const {style,shell} = this.selection();
      const option = style.key === 'beaded' ? this.stone() : style.key === 'cord' ? this.cord() : style;
      const reverse = style.key === 'beaded' && !!option?.reverseImage && this.state.wearing === 'sea-glass';
      const finishedCord = style.key === 'cord' && shell?.label === option?.finishedShell && option?.finishedImage;
      const finishedBeaded = style.key === 'beaded' && !reverse && !shell?.none && option?.finishedImages?.[shell?.key];
      const assembled = (style.key === 'gold' && !!shell?.chainImage) || !!finishedCord || !!finishedBeaded;
      const base = finishedBeaded || finishedCord || (style.key === 'gold' && shell?.chainImage) || (reverse ? option.reverseImage : option?.image || this.baseImages[style.key]);
      const clamp = (v,fallback,min,max) => typeof v === 'number' && Number.isFinite(v) ? Math.min(max,Math.max(min,v)) : fallback;
      const solid = !assembled && style.key === 'beaded' && !!shell?.beadedImage && !reverse && !shell?.none;
      return {base,reverse,option,solid,assembled,shellImage:!assembled && !reverse && !shell?.none && (solid ? shell.beadedImage : shell?.image) || null,x:8 + .84 * clamp(option?.shellX,50,0,100),y:4 + .84 * clamp((option?.shellY ?? 75) + (solid ? 2 : 0),75,0,100),width:clamp(option?.shellWidth,28,10,45) * (solid ? clamp(shell.beadedScale,100,50,150) / 100 : 1),holeX:clamp(solid ? shell.beadedHoleX : shell?.holeX,48,0,100),holeY:clamp(solid ? shell.beadedHoleY : shell?.holeY,23,0,100)};
    }
    totalPrice() {
      const {style,shell} = this.selection();
      if (style.key !== 'beaded') return style.price;
      const stone = this.stone();
      const shellPrice = Number(this.dataset.shellPrice);
      if (!stone?.priceConfirmed || !Number.isSafeInteger(stone.basePrice) || stone.basePrice < 0 || !Number.isSafeInteger(shellPrice) || shellPrice < 0) return null;
      return stone.basePrice + (shell?.none ? 0 : shellPrice);
    }
    money(price) {
      return new Intl.NumberFormat('en-US',{style:'currency',currency:'USD',minimumFractionDigits:price % 100 ? 2 : 0,maximumFractionDigits:2}).format(price / 100);
    }
    stoneDescription() {
      const stone = this.stone();
      return stone ? [stone.label,stone.beadSize,stone.length].filter(Boolean).join(' · ') : '';
    }
    summary() {
      const {style,shell} = this.selection();
      return [style.label,style.key === 'beaded' ? this.stoneDescription() : style.key === 'cord' ? [this.cord()?.label,this.cord()?.length].filter(Boolean).join(' · ') : style.length,shell ? shell.label : this.dataset.chooseShell].filter(Boolean).join(' · ');
    }
    configuration() {
      const {style,shell} = this.selection();
      if (!shell || (style.key === 'beaded' && !this.beads.includes(this.state.bead))) return null;
      if (style.key === 'cord' && !this.cord()) return null;
      return this.purchaseConfig.configurations.find(item => item.style === style.key && (item.withoutShell === true) === (shell.none === true) && (!item.shell || item.shell === shell.key) && (style.key !== 'beaded' || item.bead === this.state.bead) && (!item.cord || item.cord === this.state.cord) && item.fulfillment === 'made-to-order' && Number.isSafeInteger(item.variantId) && item.variantId > 0) || null;
    }
    canPurchase() {
      return this.purchaseConfig.enabled === true && this.totalPrice() !== null && !!this.configuration();
    }
    properties() {
      const {style,shell} = this.selection();
      if (!shell || (style.key === 'beaded' && !this.beads.includes(this.state.bead))) throw new Error(this.dataset.chooseShell);
      const properties = {'Necklace style':style.label,'Ceramic shell':shell.label,'Production':'Made to order','Production time':'Approximately three weeks before shipping','_builder':'cb-atelier-v4','_shell_reference':shell.key};
      if (style.key === 'beaded') {
        const stone = this.stone();
        properties['Stone option'] = stone.label;
        if (stone.beadSize) properties['Stone size'] = stone.beadSize;
        if (stone.length) properties['Necklace length'] = stone.length;
        properties['_stone_reference'] = this.state.bead;
      } else if (style.key === 'cord') {
        const cord = this.cord();
        if (!cord) throw new Error(this.dataset.unavailable);
        properties['Cord color'] = cord.label;
        if (cord.length) properties['Necklace length'] = cord.length;
        properties['_cord_reference'] = this.state.cord;
      } else if (style.length) {
        properties['Necklace length'] = style.length;
      }
      return properties;
    }
    payload() {
      if (!this.canPurchase()) throw new Error(this.dataset.unavailable);
      return {items:[{id:this.configuration().variantId,quantity:1,properties:this.properties()}]};
    }
    render() {
      const {style,shell} = this.selection();
      this.querySelector('[data-beads]').hidden = style.key !== 'beaded';
      this.querySelector('[data-cords]').hidden = style.key !== 'cord';
      this.querySelectorAll('input[name="cord"]').forEach(input => {input.checked = input.value === this.state.cord;});
      this.querySelectorAll('[data-shell-card]').forEach(card => {
        const item = this.shells.find(value => value.key === card.dataset.shellCard);
        const compatible = this.compatible(item,style);
        card.hidden = !compatible;
        const input = card.querySelector('input');
        input.disabled = !compatible;
        input.checked = !!shell && shell.key === item.key;
      });
      const total = this.totalPrice();
      this.querySelector('[data-total]').textContent = total === null ? this.dataset.pricePending : this.money(total);
      this.querySelector('[data-beaded-price]').textContent = style.key === 'beaded' && total !== null ? this.money(total) : this.dataset.byStone;
      this.querySelector('[data-price-breakdown]').textContent = style.key === 'beaded' && total !== null ? shell?.none ? this.dataset.necklaceOnly : `${this.dataset.breakdownLabel}: ${this.money(this.stone().basePrice)} + ${this.money(Number(this.dataset.shellPrice))}` : '';
      this.querySelectorAll('input[name="bead"]').forEach(input => {input.checked = input.value === this.state.bead;});
      this.querySelector('[data-summary]').textContent = this.summary();
      const description = style.key === 'beaded' ? this.stone()?.description : style.key === 'cord' ? this.cord()?.description : style.description;
      this.querySelector('[data-selection-description]').textContent = shell?.none ? this.dataset.noShellDescription : description || '';
      this.querySelector('[data-style-label]').textContent = style.label;
      this.querySelector('[data-option-step]').textContent = style.key === 'beaded' ? this.dataset.stoneStep : style.key === 'cord' ? this.dataset.cordStep : this.dataset.lengthStep;
      this.querySelector('[data-gold-price]').textContent = this.money(this.styles.find(s => s.key === 'gold').price);
      this.querySelector('[data-cord-price]').textContent = this.money(this.styles.find(s => s.key === 'cord').price);
      this.querySelectorAll('[data-included]').forEach(e => {e.textContent = e.dataset.included === 'beaded' && shell?.none ? this.dataset.necklaceOnly : this.dataset.includedLabel;});
      this.querySelector('[data-base-label]').textContent = style.key === 'beaded' ? this.stoneDescription() : style.key === 'cord' ? [this.cord()?.label,this.cord()?.length].filter(Boolean).join(' · ') : style.length;
      const preview = this.preview();
      const frame = this.querySelector('[data-preview-frame]');
      for (const [key,value] of Object.entries({'shell-x':preview.x,'shell-y':preview.y,'shell-width':preview.width,'hole-x':preview.holeX,'hole-y':preview.holeY})) frame.style.setProperty(`--${key}`,`${value}%`);
      frame.toggleAttribute('data-shell-only',!preview.base);
      frame.toggleAttribute('data-assembled',preview.assembled === true);
      frame.toggleAttribute('data-solid-shell',preview.solid === true);
      const baseImg = this.querySelector('[data-base-image]');
      baseImg.hidden = !preview.base;
      if (preview.base) {baseImg.src = preview.base; baseImg.alt = preview.assembled ? [style.label,shell.label].join(' · ') : this.querySelector('[data-base-label]').textContent;}
      const hardware = this.querySelector('[data-hardware-image]');
      const ring = preview.option?.frontRing;
      hardware.hidden = !(preview.solid && ring?.enabled);
      if (!hardware.hidden) {
        hardware.src = preview.base;
        hardware.style.maskImage = `radial-gradient(ellipse ${ring.rx}% ${ring.ry}% at ${ring.x}% ${ring.y}%,transparent 66%,black 70%,black 100%,transparent 104%)`;
        hardware.style.clipPath = `inset(${ring.y}% 0 0 0)`;
        frame.style.setProperty('--shell-x', `${8 + .84 * ring.x}%`);
        frame.style.setProperty('--shell-y', `${4 + .84 * (ring.y + ring.ry * .8)}%`);
      }
      const img = this.querySelector('[data-preview-image]');
      img.hidden = !preview.shellImage;
      if (preview.shellImage) {img.src = preview.shellImage; img.alt = shell.label;}
      this.querySelector('[data-photo-placeholder]').hidden = !!preview.base || !!preview.shellImage;
      this.querySelector('[data-wearing-views]').hidden = !(style.key === 'beaded' && this.stone()?.reverseImage);
      this.querySelector('[data-alternate-note]').hidden = !preview.reverse;
      this.querySelectorAll('input[name="wearing"]').forEach(input => {input.checked = input.value === (preview.reverse ? 'sea-glass' : 'clasp');});
      this.querySelector('[data-shell-label]').textContent = shell ? shell.label : this.dataset.chooseShell;
      this.querySelector('[data-review]').disabled = !shell;
      const button = this.querySelector('[data-add]');
      button.disabled = !this.canPurchase() || this.busy === true;
      button.textContent = this.busy ? this.dataset.addingLabel : this.canPurchase() ? this.dataset.addLabel : this.dataset.previewLabel;
      this.querySelector('[data-cord-note]').hidden = style.key !== 'cord';
      const input = this.querySelector('[data-variant-id]');
      input.value = this.configuration()?.variantId || '';
      const properties = this.querySelector('[data-properties]');
      properties.replaceChildren();
      if (shell && (style.key !== 'cord' || this.cord())) Object.entries(this.properties()).forEach(([key,value]) => {
        const field = document.createElement('input');
        field.type = 'hidden'; field.name = `properties[${key}]`; field.value = value; properties.append(field);
      });
    }
    async add(event) {
      event.preventDefault();
      if (this.busy) return;
      const status = this.querySelector('[data-status]');
      if (!this.canPurchase()) {status.textContent = this.dataset.unavailable; return;}
      const payload = this.payload();
      const expectedPrice = this.totalPrice();
      const configuration = this.configuration();
      this.busy = true;
      this.render();
      try {
        const response = await fetch(`${this.dataset.rootUrl}products/${this.dataset.productHandle}.js`,{headers:{Accept:'application/json'}});
        if (!response.ok) throw new Error(this.dataset.unavailable);
        const product = await response.json();
        const variant = product.variants.find(item => item.id === configuration.variantId);
        if (product.id !== 9145096503501 || !variant?.available || variant.price !== expectedPrice) throw new Error(this.dataset.unavailable);
        const result = await fetch(`${this.dataset.rootUrl}cart/add.js`,{method:'POST',headers:{'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify(payload)});
        const cart = await result.json();
        if (!result.ok) throw new Error(cart.description || this.dataset.error);
        window.location.assign(this.dataset.cartUrl);
      } catch (error) {
        status.textContent = error.message || this.dataset.error;
      } finally {
        this.busy = false;
        this.render();
      }
    }
  }
  customElements.define('cb-atelier',Atelier);
})();
